import { ESubscriptionPlan } from '@repo-sapiens/constants';
import mongoose, { InferSchemaType } from 'mongoose';
import { Document } from 'mongoose';

export type T_IntegrationType =
  | 'drive'
  | 'gmail'
  | 'calendar'
  | 'contacts'
  | 'slack'
  | 'readAI'
  | 'github'
  | 'figma'
  | 'zoom';

export enum E_EmailArchiveLevel {
  Aggressive = 'aggressive',
  Default = 'default',
  Custom = 'custom',
  Off = 'off',
}

type T_IntegrationData = {
  accessToken?: string;
  refreshToken?: string;
  userId?: string;
  email?: string;
  userName?: string;
  team?: { id: string; name: string };
  enterprise?: { id: string; name: string };
  expiresIn?: Date;
  scope?: string;
  scopes?: string[];
};
export type T_Integration = {
  id: string;
  type: T_IntegrationType;
  data: T_IntegrationData;
};

export type PushNotificationSettings = {
  hasNotification?: boolean;
  morningSummary?: boolean;
  notSendIfNoEvents?: boolean;
  deliveryTime?: string;
  meetingPrep?: boolean;
  notifyPerAttendee?: boolean;
  timeToReceivePrep?: number;
};

export type EmailSettings = PushNotificationSettings & {
  canReceiveUpdates?: boolean;
  canReceiveNews?: boolean;
  canReceivePromotions?: boolean;
};

export type T_UserSettings = {
  hasEmailNotification?: boolean;
  hasSlackNotification?: boolean;
  email?: EmailSettings;
  pushNotification?: PushNotificationSettings;
  timeZone?: string;
  language?: string;
  createTaskShortcut?: string;
  createNoteShortcut?: string;
  location?: {
    region?: string;
    city?: string;
    country?: string;
    timezone?: string;
    postal?: string;
  };
};

type CreatedGmailLabel = {
  id: string;
  name: string;
  backupName: string;
};

export enum E_MultiplayerServiceName {
  InboxManagement = 'inbox-management',
  DraftReply = 'draft-reply',
  DailyPodcast = 'daily-podcast',
  ActionItems = 'action-items',
  MeetingPrep = 'meeting-prep',
  MeetingAssistant = 'meeting-assistant',
}

export type MeetingBotSettings = {
  joinMeetings: 'all' | 'external' | 'internal' | '1:1' | 'disabled';
  followUpEmail: 'all' | 'external' | 'internal' | '1:1' | 'disabled';
  meetingNoteEmail: 'all' | 'external' | 'internal' | 'me' | 'disabled';
};

const UserSchema = new mongoose.Schema({
  firstName: { type: String },
  lastName: { type: String },
  profileUrl: { type: String },
  email: { type: String, required: true, unique: true },
  created: { type: Date, required: true },
  integrations: { type: [mongoose.Schema.Types.Mixed] },
  deleted: { type: Date },
  lastSession: { type: Date },
  partialAccount: { type: Boolean },
  deactivated: { type: Boolean, default: false },
  deactivatedAt: { type: Date },
  invalidGrant: { type: Boolean, default: false },
  invalidGrantAt: { type: Date },
  rateLimitExceeded: { type: Boolean, default: false },
  rateLimitExceededAt: { type: Date },
  settings: {
    type: mongoose.Schema.Types.Mixed,
    default: {
      hasEmailNotification: true,
      hasSlackNotification: true,
      email: {
        hasNotification: true,
        morningSummary: true,
        notSendIfNoEvents: false,
        deliveryTime: '06:00',
        meetingPrep: true,
        notifyPerAttendee: false,
        timeToReceivePrep: 15,
        canReceiveUpdates: true,
        canReceiveNews: true,
        canReceivePromotions: true,
      },
      pushNotification: {
        hasNotification: true,
        morningSummary: true,
        notSendIfNoEvents: false,
        deliveryTime: '06:00',
        meetingPrep: true,
        notifyPerAttendee: false,
        timeToReceivePrep: 15,
      },
    },
  },
  createdGmailLabels: { type: [mongoose.Schema.Types.Mixed] },
  pushNotificationToken: { type: mongoose.Schema.Types.Mixed },
  pushNotificationTokens: { type: [mongoose.Schema.Types.Mixed] },
  creditBalance: { type: Number, default: 10000000 },
  tempLocation: { type: String },
  preferences: { type: mongoose.Schema.Types.Mixed },
  notifications: {
    type: mongoose.Schema.Types.Mixed,
    default: {
      hasNotification: true,
      morningSummary: true,
      notSendIfNoEvents: false,
      endOfDayNotification: true,
      endOfWeekNotification: true,
      sundayNotification: true,
      timeToReceivePrep: 15,
    },
  },
  source: { type: String, default: 'ai.multiplayer.work' },
  subscription: { type: mongoose.Schema.Types.Mixed },
  stripeCustomerId: { type: String },
  firstSubscriptionAt: { type: Date },
  services: { type: [String] },
  vipUser: { type: Boolean, default: false },
  onboardingCompleted: { type: Boolean, default: false },
  meetingBotSettings: {
    type: mongoose.Schema.Types.Mixed,
    default: {
      joinMeetings: 'all',
      followUpEmail: 'all',
      meetingNoteEmail: 'all',
    },
  },
  archiveLevel: {
    type: String,
    default: E_EmailArchiveLevel.Default,
  },
  primaryLanguage: { type: String, default: 'English' },
});

export type T_UserBase = InferSchemaType<typeof UserSchema>;

// Extend to provide proper typing for complex fields
export interface T_User
  extends Omit<
    T_UserBase,
    | 'integrations'
    | 'settings'
    | 'createdGmailLabels'
    | 'pushNotificationToken'
    | 'pushNotificationTokens'
    | 'preferences'
    | 'subscription'
    | 'services'
    | 'meetingBotSettings'
    | 'archiveLevel'
    | 'notifications'
  > {
  _id: string;
  integrations?: T_Integration[];
  settings?: T_UserSettings;
  createdGmailLabels?: CreatedGmailLabel[];
  pushNotificationToken?: {
    token: string;
    os: string;
  };
  pushNotificationTokens?: {
    token: string;
    os: string;
    brand?: string;
    model?: string;
    systemVersion?: string;
    isSimulator?: boolean;
  }[];
  preferences?: {
    location?: string;
    secondaryLocation?: string;
    language?: string;
    timezone?: string;
    firstName?: string;
    lastName?: string;
    lastChanged: Date;
    emailSignature?: string;
    calendarLink?: string;
  };
  notifications?: {
    hasNotification: boolean;
    morningSummary: boolean;
    notSendIfNoEvents: boolean;
    endOfDayNotification: boolean;
    endOfWeekNotification: boolean;
    sundayNotification: boolean;
    timeToReceivePrep: number;
  };
  subscription?: {
    plan: ESubscriptionPlan;
    startAt?: Date;
    endAt?: Date;
    autoRenew: boolean;
    isTrial: boolean;
    billingCycle: string;
    canceledAt?: Date;
  };
  services?: E_MultiplayerServiceName[];
  meetingBotSettings?: MeetingBotSettings;
  archiveLevel?: E_EmailArchiveLevel;
  firstSubscriptionAt?: Date;
}

export type T_UserDocument = T_User & Document;

export { UserSchema };
